import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../GlobalContext";
import TablePagination from "../Utils/TablePagination";
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  IndianRupee,
  Wallet,
  FileDown,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Sparkles,
  Clock,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";

// amount formatter
const fmt2 = (num) =>
  Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const DriverAnalytics = () => {
  const { driverId, username } = useParams();
  const navigate = useNavigate();

  const { baseurl, token, AdminId, setIsUnauthorised } = useGlobalContext();

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const [reports, setReports] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);

  const showValue = (val, isAmount = false) => {
    if (val === null || val === undefined) return "N/A";
    if (isAmount) return `₹ ${fmt2(val)}`;
    return val;
  };

  const handleExport = () => {
    if (!reports || reports.length === 0) {
      alert("No reports available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: reports,
      filePrefix: `Driver_Report_${driverId}`,
      sheetName: "Driver Reports",
    });
  };

  // ANALYTICS API
  const fetchDriverAnalytics = async (driverCode) => {
    if (!driverCode) return;

    setLoading(true);
    try {
      const payload = {
        adminId: AdminId,
        driverCode,
        fromDate,
        toDate,
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v2.0/getDriverAnalytics`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      setAnalytics(res.data?.data || null);
    } catch (error) {
      console.error("Analytics API Error:", error);
      if (error?.response?.status === 401) {
        setIsUnauthorised(true);
      }
    } finally {
      setLoading(false);
    }
  };

  //  REPORTS API
  const fetchDriverReports = async (driverCode) => {
    if (!driverCode) return;

    setTaskLoading(true);
    try {
      const payload = {
        adminId: AdminId,
        driverCode,
        fromDate,
        toDate,
      };

      const res = await axios.post(
        `${baseurl}/api/Task/v1.0/getDriverReport`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      setReports(res.data?.data.reverse() || []);
    } catch (error) {
      console.error("Reports API Error:", error);
      if (error?.response?.status === 401) {
        setIsUnauthorised(true);
      }
    } finally {
      setTaskLoading(false);
    }
  };

  useEffect(() => {
    if (driverId) {
      fetchDriverAnalytics(driverId);
      fetchDriverReports(driverId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const handleApply = () => {
    setCurrentPage(1);
    fetchDriverAnalytics(driverId);
    fetchDriverReports(driverId);
  };

  const reportsPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = reports.length;

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;

  const currentReports = reports.slice(indexOfFirstReport, indexOfLastReport);

  function formatMinutes(minutes) {
    if (minutes < 60) {
      return `${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return remainingMinutes === 0
      ? `${hours} h`
      : `${hours} h ${remainingMinutes} min`;
  }

  // ---------- UI HELPERS (BADGES + PARSERS) ----------
  const Badge = ({
    children,
    className = "",
    title,
  }) => (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${className}`}
    >
      {children}
    </span>
  );

  // estimated_time example: "8 mins", "11 mins"
  const parseEstimatedMinutes = (estimated) => {
    if (!estimated) return null;
    const str = String(estimated).toLowerCase();

    // Try to pull first integer number
    const m = str.match(/(\d+)/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  };

  const normalizeTaskUpdate = (val) => String(val || "").trim().toLowerCase();

  // waiting_time examples:
  // "Driver waited for 7 mins" (against driver)
  // "Restaurant waited for 1 mins" (in driver favour)
  const getWaitingMeta = (waiting) => {
    const w = String(waiting || "").trim();
    const wl = w.toLowerCase();
    if (!w) return { kind: "none", text: "No waiting record found" };

    if (wl.startsWith("restaurant waited")) {
      return { kind: "driver_favor", text: w };
    }
    if (wl.startsWith("driver waited")) {
      return { kind: "against_driver", text: w };
    }
    return { kind: "neutral", text: w };
  };

  const renderDeliveryTimeCell = (task) => {
    const deliveryMin = Number(task?.delivery_time);
    const estMin = parseEstimatedMinutes(task?.estimated_time);

    if (!Number.isFinite(deliveryMin)) return "N/A";

    const isLate =
      Number.isFinite(estMin) && estMin !== null ? deliveryMin > estMin : false;

    // Rule: if delivery time > estimated time => show down arrow + red pill badge with delivery time text
    if (isLate) {
      return (
        <Badge
          title={`Late: ${deliveryMin} min vs est ${estMin} min`}
          className="bg-red-50 text-red-700 border border-red-200"
        >
          <ArrowDown size={14} />
          <span>{formatMinutes(deliveryMin)}</span>
        </Badge>
      );
    }

    // Optional: on-time / faster => green subtle pill (keeps table consistent)
    return (
      <Badge
        title={
          Number.isFinite(estMin)
            ? `On time: ${deliveryMin} min vs est ${estMin} min`
            : `Delivery: ${deliveryMin} min`
        }
        className="bg-green-50 text-green-700 border border-green-200"
      >
        <ArrowUp size={14} />
        <span>{formatMinutes(deliveryMin)}</span>
      </Badge>
    );
  };

  const renderTaskUpdateCell = (taskUpdate) => {
    const t = normalizeTaskUpdate(taskUpdate);

    // Rule: Daily => blue outline badge with icon
    if (t === "daily") {
      return (
        <Badge className="bg-transparent text-blue-700 border border-blue-300">
          <CalendarDays size={14} />
          <span>Daily</span>
        </Badge>
      );
    }

    // Rule: Extra => green badge with icon
    if (t === "extra") {
      return (
        <Badge className="bg-green-50 text-green-700 border border-green-200">
          <Sparkles size={14} />
          <span>Extra</span>
        </Badge>
      );
    }

    // fallback
    return (
      <Badge className="bg-gray-50 text-gray-700 border border-gray-200">
        <Clock size={14} />
        <span>{taskUpdate ?? "N/A"}</span>
      </Badge>
    );
  };

  const renderWaitingTimeCell = (waiting_time) => {
    const meta = getWaitingMeta(waiting_time);

    // Rule: in driver favour vs against driver
    if (meta.kind === "against_driver") {
      return (
        <Badge
          title="In driver favour (restaurant waited)"
          className="bg-emerald-50 text-emerald-700 border border-emerald-200"
        >
          <ThumbsUp size={14} />
          <span>{meta.text}</span>
        </Badge>
      );
    }

    if (meta.kind === "driver_favor") {
      return (
        <Badge
          title="Against driver (driver waited)"
          className="bg-amber-50 text-amber-800 border border-amber-200"
        >
          <ThumbsDown size={14} />
          <span>{meta.text}</span>
        </Badge>
      );
    }

    if (meta.kind === "none") {
      return <span className="text-gray-400">{meta.text}</span>;
    }

    return (
      <Badge className="bg-gray-50 text-gray-700 border border-gray-200">
        <Clock size={14} />
        <span>{meta.text}</span>
      </Badge>
    );
  };
const formatDistance = (meters) => {
  if (meters === null || meters === undefined || isNaN(meters)) {
    return "N/A";
  }

  const m = Number(meters);

  if (m < 1000) {
    return `${Math.round(m)} m`;
  }

  return `${(m / 1000).toFixed(2)} km`;
};

  return (
    <>
      <div className="p-6 min-h-screen space-y-5">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                Packky Driver Analytics — {decodeURIComponent(username || "")}
                <span className="text-gray-400 font-normal"> ({driverId})</span>
              </h2>

              <p className="text-sm text-gray-500">
                Filter, analyze, process & export
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end justify-end">
            <div>
              <label className="block text-xs font-medium mb-1">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border p-2 rounded text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border p-2 rounded text-sm"
              />
            </div>

            <button
              onClick={handleApply}
              className="min-w-[96px] h-10 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center justify-center"
            >
              Apply
            </button>

            <button
              className="min-w-[96px] h-10 bg-green-600 hover:bg-green-700 text-white rounded text-sm flex items-center justify-center gap-2"
              onClick={handleExport}
            >
              <FileDown size={16} />
              <span>Export</span>
            </button>

            <button
              onClick={() => navigate(-1)}
              className="min-w-[96px] h-10 border rounded text-sm hover:bg-gray-50 flex items-center justify-center"
            >
              ← Back
            </button>
          </div>
        </div>

        <h2 className="text-xl font-semibold">Driver Analytics</h2>

        {loading && <p className="text-sm text-gray-500">Loading analytics...</p>}

        {!loading && analytics && (
          <div className="grid gap-3 md:grid-cols-5">
            <div className="group rounded-2xl border border-l-4 border-l-blue-500 bg-white p-4 transition-all hover:bg-blue-100 hover:border-l-blue-700">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-blue-600 group-hover:text-blue-700" />
                <div>
                  <div className="text-xs text-gray-500">Assigned Orders</div>
                  <div className="text-lg font-semibold">
                    {showValue(analytics.assigned_orders)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-green-500 bg-white p-4 transition-all hover:bg-green-100 hover:border-l-green-700">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 group-hover:text-green-700" />
                <div>
                  <div className="text-xs text-gray-500">Successful Orders</div>
                  <div className="text-lg font-semibold text-green-700">
                    {showValue(analytics.current_successful_orders)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-red-500 bg-white p-4 transition-all hover:bg-red-100 hover:border-l-red-700">
              <div className="flex items-center gap-3">
                <XCircle className="h-6 w-6 text-red-600 group-hover:text-red-700" />
                <div>
                  <div className="text-xs text-gray-500">Declined Orders</div>
                  <div className="text-lg font-semibold text-red-600">
                    {showValue(analytics.declined_orders)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-indigo-500 bg-white p-4 transition-all hover:bg-indigo-100 hover:border-l-indigo-700">
              <div className="flex items-center gap-3">
                <IndianRupee className="h-6 w-6 text-indigo-600 group-hover:text-indigo-700" />
                <div>
                  <div className="text-xs text-gray-500">Per Day Earning</div>
                  <div className="text-lg font-semibold text-indigo-700">
                    {showValue(analytics.per_day, true)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-emerald-500 bg-white p-4 transition-all hover:bg-emerald-50 hover:border-l-emerald-600">
              <div className="flex items-center gap-3">
                <Wallet className="h-6 w-6 text-emerald-600 group-hover:text-emerald-700" />
                <div>
                  <div className="text-xs text-gray-500">Per Order Earning</div>
                  <div className="text-lg font-semibold text-emerald-700">
                    {showValue(analytics.per_order, true)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !analytics && (
          <p className="text-sm text-red-400">
            No analytics data available for selected date range.
          </p>
        )}

        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div>
            <h2 className="text-xl font-semibold">Driver Reports</h2>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white border rounded-lg shadow-sm mt-6">
          {taskLoading ? (
            <p className="p-4 text-sm text-gray-500">Loading reports...</p>
          ) : reports.length === 0 ? (
            <p className="p-4 text-sm text-gray-400">
              No reports found for selected date range.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-orange-400 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Task Id</th>
                    <th className="px-4 py-2 text-left">Order Id</th>
                    <th className="px-4 py-2 text-left">Description</th>
                    <th className="px-4 py-2 text-left">Estimate Time</th>
                    <th className="px-4 py-2 text-left">Distance</th>
                    <th className="px-4 py-2 text-left">Delivery Date</th>
                    <th className="px-4 py-2 text-left">Waiting Time</th>
                    <th className="px-4 py-2 text-left">Task Update</th>
                    <th className="px-4 py-2 text-left">Charges</th>
                    <th className="px-4 py-2 text-left">Delivery Time</th>
                  </tr>
                </thead>
                <tbody>
                  {currentReports.map((task, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2">{task.pk_task_id}</td>
                      <td className="px-4 py-2">{task.order_id}</td>
                      <td className="px-4 py-2">{task.task_description}</td>
                      <td className="px-4 py-2">{task.estimated_time}</td>
                     <td className="px-4 py-2">{formatDistance(task.distance)}</td>

                      <td className="px-4 py-2">
                        {task.delivery_date ?? "N/A"}
                      </td>

                      {/* Waiting time — driver favour vs against driver */}
                      <td className="px-4 py-2">
                        {renderWaitingTimeCell(task.waiting_time)}
                      </td>

                      {/* Task update — Daily(blue outline) / Extra(green) */}
                      <td className="px-4 py-2">
                        {renderTaskUpdateCell(task.task_update)}
                      </td>

                      <td className="px-4 py-2">₹ {fmt2(task.charges)}</td>

                      {/* Delivery time — late red pill + down arrow */}
                      <td className="px-4 py-2">
                        {renderDeliveryTimeCell(task)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

            </div>
          )}
        </div>
          <TablePagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={reportsPerPage}
            onPageChange={setCurrentPage}
          />
      </div>
    </>
  );
};

export default DriverAnalytics;
